import React, { useState, useRef, useEffect } from 'react';
import styles from './PlabAI.module.css';
import * as d3 from 'd3';
import * as d3Hexbin from 'd3-hexbin';
import { ICON_PATHS } from '../../constants/iconPaths';
import { Helmet } from 'react-helmet-async';

const TABS = [
  {
    id: 'distance',
    label: '뛴거리',
    value: '8.3km'
  },
  {
    id: 'touch',
    label: '볼터치',
    value: '124'
  },
  {
    id: 'pass',
    label: '패스 성공률',
    value: '85%'
  },
  {
    id: 'possession',
    label: '소유시간',
    value: '3\'22\'\''
  },
  {
    id: 'position',
    label: '포지션',
    value: 'MID'
  }
];

function PlabAI() {
  const [activeTab, setActiveTab] = useState('distance');
  const [selectedPlayer, setSelectedPlayer] = useState(1);
  const scrollRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  
  
  // 선수별 터치 포인트를 저장할 ref 생성
  const touchPointsRef = useRef({});

  // 탭을 중앙으로 스크롤하는 함수
  const scrollToCenter = (element) => {
    if (!scrollRef.current || !element) return;
    
    const container = scrollRef.current;
    const containerWidth = container.offsetWidth;
    const elementWidth = element.offsetWidth;
    const elementLeft = element.offsetLeft;
    
    const centerPosition = elementLeft - (containerWidth / 2) + (elementWidth / 2);
    
    container.scrollTo({
      left: centerPosition,
      behavior: 'smooth'
    });
  };

  const handleTabClick = (tabId, element) => {
    setActiveTab(tabId);
    setSelectedPlayer(1);
    scrollToCenter(element);
  };

  // 스크롤 이벤트 핸들러
  const handleScroll = (e) => {
    const container = e.target;
    const totalWidth = container.scrollWidth;
    const viewWidth = container.clientWidth;
    const maxScroll = totalWidth - viewWidth;
    
    if (container.scrollLeft === 0) {
      container.scrollLeft = maxScroll / 2;
    } else if (container.scrollLeft >= maxScroll) {
      container.scrollLeft = maxScroll / 2;
    }
    
    setScrollPosition(container.scrollLeft);
  };

  // 초기 스크롤 위치 설정
  useEffect(() => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const totalWidth = container.scrollWidth;
      const viewWidth = container.clientWidth;
      container.scrollLeft = (totalWidth - viewWidth) / 2;
    }
  }, []);

  // 20개의 탭 세트를 생성 (중간에서 시작하여 양쪽으로 스크롤 가능)
  const repeatedTabs = Array(20).fill(TABS).flat();

  const TOUCH_DATA = [
    { number: 1, value: '142', touchCount: 142 },
    { number: 2, value: '189', touchCount: 189 },
    { number: 3, value: '96', touchCount: 96 },
    { number: 4, value: '114', touchCount: 114 },
    { number: 5, value: '156', touchCount: 156 },
  ];

  const DISTANCE_DATA = [
    { number: 1, value: '8.9km', heatmapCount: 130 },
    { number: 2, value: '8.5km', heatmapCount: 28 },
    { number: 3, value: '8.2km', heatmapCount: 25 },
    { number: 4, value: '7.8km', heatmapCount: 23 },
    { number: 5, value: '7.5km', heatmapCount: 20 },
  ];

  const POSITION_DATA = [
    { number: 1, position: 'LOW' },
    { number: 2, position: 'MID' },
    { number: 3, position: 'HIGH' },
    { number: 4, position: 'LOW' },
    { number: 5, position: 'MID' },
  ];

  // 소유시간 데이터 추가
  const POSSESSION_DATA = [
    { number: 1, value: '3\'22\'\'', seconds: 202 },
    { number: 2, value: '2\'45\'\'', seconds: 165 },
    { number: 3, value: '3\'10\'\'', seconds: 190 },
    { number: 4, value: '2\'38\'\'', seconds: 158 },
    { number: 5, value: '2\'51\'\'', seconds: 171 },
  ];

  // 히트맵 데이터를 저장할 ref
  const heatmapDataRef = useRef({});

  // 선수별 히트맵 패턴 데이터 정의
  const PLAYER_HEATMAP_PATTERNS = {
    1: { // 많이 뛴 선수
      zones: [
        { x: 0.1, y: 0.1, intensity: 0.9 },
        { x: 0.9, y: 0.9, intensity: 0.9 },
        { x: 0.3, y: 0.2, intensity: 0.9 }, // 왼쪽 공격
        { x: 0.2, y: 0.3, intensity: 0.5 },
        { x: 0.2, y: 0.5, intensity: 0.5 }, // 왼쪽 수비
        { x: 0.8, y: 0.2, intensity: 0.9 }, // 오른쪽 공격
        { x: 0.8, y: 0.5, intensity: 0.7 }, // 오른쪽 수비
        { x: 0.8, y: 0.7, intensity: 0.5 }, // 오른쪽 수비
      ]
    },
    2: { // 중앙 집중형
      zones: [
        { x: 0.5, y: 0.5, intensity: 0.9 }, // 중앙
        { x: 0.4, y: 0.4, intensity: 0.7 }, // 중앙 왼쪽
        { x: 0.6, y: 0.4, intensity: 0.7 }, // 중앙 오른쪽
      ]
    },
    3: { // 수비형
      zones: [
        { x: 0.3, y: 0.7, intensity: 0.9 }, // 왼쪽 수비
        { x: 0.7, y: 0.7, intensity: 0.9 }, // 오른쪽 수비
        { x: 0.5, y: 0.6, intensity: 0.7 }, // 중앙 수비
      ]
    },
    4: { // 수비형
      zones: [
        { x: 0.3, y: 0.7, intensity: 0.9 }, // 왼쪽 수비
        { x: 0.7, y: 0.7, intensity: 0.9 }, // 오른쪽 수비
        { x: 0.5, y: 0.6, intensity: 0.7 }, // 중앙 수비
      ]
    },
    5: { // 수비형
      zones: [
        { x: 0.3, y: 0.7, intensity: 0.9 }, // 왼쪽 수비
        { x: 0.7, y: 0.7, intensity: 0.9 }, // 오른쪽 수비
        { x: 0.5, y: 0.6, intensity: 0.7 }, // 중앙 수비
      ]
    },
  };

  // 벌집 그리드 위치 생성 함수 수정
  const generateHexGrid = () => {
    const grid = [];
    const rows = 8;
    const cols = 6;
    const hexWidth = 100 / cols;
    const hexHeight = (100 / rows) * (Math.sqrt(3) / 2);

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const x = col * hexWidth + (row % 2 ? hexWidth / 2 : 0); // 홀수 행 오프셋
        const y = row * hexHeight;
        grid.push({ x, y });
      }
    }
    return grid;
  };


  // 컴포넌트 마운트 시 각 선수의 터치 포인트와 히트맵 데이터 생성
  useEffect(() => {
    TOUCH_DATA.forEach(player => {
      if (!touchPointsRef.current[player.number]) {
        touchPointsRef.current[player.number] = Array.from(
          { length: player.touchCount }, 
          () => ({
            x: Math.random() * 100,
            y: Math.random() * 100,
          })
        );
      }
    });

    DISTANCE_DATA.forEach(player => {
      if (!heatmapDataRef.current[player.number]) {
        const points = [];
        const playerPattern = PLAYER_HEATMAP_PATTERNS[player.number];
        
        if (playerPattern) {
          playerPattern.zones.forEach(zone => {
            const centerX = 480 * zone.x;
            const centerY = 640 * zone.y;
            const pointCount = Math.floor(zone.intensity * 100);
            
            for (let i = 0; i < pointCount; i++) {
              points.push([
                centerX + (Math.random() - 0.5) * 480 * 0.2,
                centerY + (Math.random() - 0.5) * 640 * 0.2
              ]);
            }
          });
        }
        
        heatmapDataRef.current[player.number] = points;
      }
    });
  }, []);

  // 컴포넌트 마운트 시 한 번만 실행
  useEffect(() => {
    // 모든 선수의 히트맵 데이터 미리 생성
    DISTANCE_DATA.forEach(player => {
      if (!heatmapDataRef.current[player.number]) {
        const points = [];
        const playerPattern = PLAYER_HEATMAP_PATTERNS[player.number];
        
        if (playerPattern) {
          playerPattern.zones.forEach(zone => {
            const centerX = 480 * zone.x;
            const centerY = 640 * zone.y;
            const pointCount = Math.floor(zone.intensity * 100);
            
            for (let i = 0; i < pointCount; i++) {
              points.push([
                centerX + (Math.random() - 0.5) * 480 * 0.2,
                centerY + (Math.random() - 0.5) * 640 * 0.2
              ]);
            }
          });
        }
        
        heatmapDataRef.current[player.number] = points;
      }
    });
  }, []); // 빈 의존성 배열로 마운트 시에만 실행

  // 히트맵 그리기
  useEffect(() => {
    // 이전 히트맵 제거
    d3.select('#heatmap').selectAll('*').remove();

    if (activeTab === 'distance' && selectedPlayer && heatmapDataRef.current[selectedPlayer]) {
      const width = 480;
      const height = 640;
      
      const svg = d3.select('#heatmap')
        .append('svg')
        .attr('width', '100%')
        .attr('height', '100%')
        .attr('viewBox', `0 0 ${width} ${height}`);

      const hexbin = d3Hexbin.hexbin()
        .radius(20)
        .extent([[0, 0], [width, height]]);

      const points = heatmapDataRef.current[selectedPlayer];
      const bins = hexbin(points);

      const getOpacity = (value) => {
        const max = d3.max(bins, d => d.length);
        const step = max / 4;
        
        if (value <= step) return 0.3;
        if (value <= step * 2) return 0.5;
        if (value <= step * 3) return 0.7;
        return 0.9;
      };

      svg.append('g')
        .selectAll('path')
        .data(bins)
        .enter()
        .append('path')
        .attr('d', hexbin.hexagon())
        .attr('transform', d => `translate(${d.x},${d.y})`)
        .attr('fill', 'var(--red-500)')
        .attr('opacity', d => getOpacity(d.length))
        .attr('stroke', 'none');
    }
  }, [activeTab, selectedPlayer]); // activeTab과 selectedPlayer가 변경될 때만 실행

  // 패스 정확도 데이터
  const PASS_DATA = [
    { number: 1, value: '76%', accuracy: 76 },
    { number: 2, value: '82%', accuracy: 82 },
    { number: 3, value: '68%', accuracy: 68 },
    { number: 4, value: '73%', accuracy: 73 },
    { number: 5, value: '79%', accuracy: 79 },
  ];

  // 양방향 패스 데이터 구조로 변경
  const PASS_INTERACTION_DATA = {
    1: [
      { to: 2, sent: 48, received: 12, distance: 0.6 },
      { to: 3, sent: 8, received: 10, distance: 0.8 },
      { to: 4, sent: 12, received: 5, distance: 0.4 },
      { to: 5, sent: 5, received: 7, distance: 0.7 },
    ],
    2: [
      { to: 1, sent: 12, received: 15, distance: 0.6 },
      { to: 3, sent: 10, received: 8, distance: 0.5 },
      { to: 4, sent: 7, received: 9, distance: 0.7 },
      { to: 5, sent: 14, received: 11, distance: 0.3 },
    ],
    // ... 다른 선수들의 데이터
  };

  const getPlayerPosition = (index, distance) => {
    // 각 모서리 위치 계산 (시계방향으로)
    const angles = {
      0: -45,  // 우상단
      1: -135, // 좌상단
      2: -225, // 좌하단
      3: -315  // 우하단
    };
    
    return {
      angle: angles[index],
      distance: Math.max(100, distance * 200)
    };
  };

  const getNodeSize = (sent, received) => {
    const totalPasses = sent + received;
    // 기본 크기 50px, 패스 횟수에 따라 최대 100px까지
    return Math.min(20 + totalPasses * 2, 80);
  };

  // 볼 운반 경로 데이터를 저장할 ref 생성
  const ballMovementPathsRef = useRef({});

  // 컴포넌트 마운트 시 각 선수의 볼 운반 경로 데이터 생성
  useEffect(() => {
    POSSESSION_DATA.forEach(player => {
      if (!ballMovementPathsRef.current[player.number]) {
        // 각 선수마다 3~6개의 경로 생성
        const pathCount = 3 + Math.floor(Math.random() * 4);
        const paths = [];
        
        for (let i = 0; i < pathCount; i++) {
          // 시작점과 끝점 랜덤 생성
          const startX = 10 + Math.random() * 80;
          const startY = 10 + Math.random() * 80;
          
          // 경로의 길이에 따라 끝점 결정 (소유시간이 길수록 더 긴 경로)
          const distance = (player.seconds / 200) * 30; // 최대 30% 이동
          const angle = Math.random() * 2 * Math.PI; // 랜덤 각도
          
          const endX = Math.max(5, Math.min(95, startX + Math.cos(angle) * distance));
          const endY = Math.max(5, Math.min(95, startY + Math.sin(angle) * distance));
          
          // 경로의 두께는 소유시간에 비례
          const thickness = 1 + (player.seconds / 200) * 3; // 1~4px
          
          paths.push({
            startX,
            startY,
            endX,
            endY,
            thickness,
            // 각 경로마다 다른 투명도 (0.3~0.8)
            opacity: 0.3 + Math.random() * 0.5
          });
        }
        
        ballMovementPathsRef.current[player.number] = paths;
      }
    });
  }, []);

  // TeamStats 컴포넌트 추가
  const TeamStats = ({ activeTab, selectedPlayer, setSelectedPlayer }) => {
    // 각 탭에 맞는 데이터 선택
    const getDataForTab = () => {
      switch (activeTab) {
        case 'touch':
          return TOUCH_DATA;
        case 'distance':
          return DISTANCE_DATA;
        case 'position':
          return POSITION_DATA;
        case 'pass':
          return PASS_DATA;
        case 'possession':
          return POSSESSION_DATA;
        default:
          return [];
      }
    };


    return (
      <section className={styles.statsSection}>
        <h3>Team Stats</h3>
        <div className={styles.playerList}>
          {activeTab === 'touch' && (() => {
            // 최대값 찾기
            const maxValue = Math.max(...TOUCH_DATA.map(player => parseInt(player.value)));
            
            return TOUCH_DATA.map((player) => {
              // 현재 값을 숫자로 변환 (예: '42회' -> 42)
              const numValue = parseInt(player.value);
              // 상대적 너비 계산 (최대값 대비 퍼센트)
              const barWidth = (numValue / maxValue) * 100;
              
              return (
                <div 
                  key={player.number} 
                  className={`${styles.playerItem} ${selectedPlayer === player.number ? styles.active : ''}`}
                  onClick={() => setSelectedPlayer(player.number)}
                >
                    <div className={styles.playerBarContainer}>
                        <span className={styles.playerNumber}>#{player.number}</span>
                        <div 
                            className={styles.playerBar} 
                            style={{ width: `${barWidth}%` }}
                        ></div>
                    </div>
                  <span className={styles.playerValue}>{player.value}</span>
                </div>
              );
            });
          })()}
          
          {activeTab === 'distance' && (() => {
            // 최대값 찾기
            const maxValue = Math.max(...DISTANCE_DATA.map(player => parseFloat(player.value)));
            
            return DISTANCE_DATA.map((player) => {
              // 현재 값을 숫자로 변환 (예: '8.2km' -> 8.2)
              const numValue = parseFloat(player.value.replace('km', ''));
              // 상대적 너비 계산 (최대값 대비 퍼센트)
              const barWidth = (numValue / maxValue) * 100;
              
              return (
                <div 
                  key={player.number} 
                  className={`${styles.playerItem} ${selectedPlayer === player.number ? styles.active : ''}`}
                  onClick={() => setSelectedPlayer(player.number)}
                >
                  <div className={styles.playerBarContainer}>
                    <span className={styles.playerNumber}>#{player.number}</span>
                    <div 
                        className={styles.playerBar} 
                        style={{ width: `${barWidth}%` }}
                    ></div>
                  </div>
                  <span className={styles.playerValue}>{player.value}</span>
                </div>
              );
            });
          })()}
          
          {activeTab === 'position' && POSITION_DATA.map((player) => (
            <div 
              key={player.number} 
              className={styles.playerItem}
            >
              <span className={styles.playerNumber}>#{player.number}</span>
              <span className={styles.playerValue}>{player.position}</span>
            </div>
          ))}
          
          {activeTab === 'pass' && (() => {
            // 최대값 찾기 (성공한 패스 수 기준)
            const maxValue = Math.max(...PASS_DATA.map(player => {
              const [success] = player.value.split('/');
              return parseInt(success);
            }));
            
            return PASS_DATA.map((player) => {
              // 현재 값을 숫자로 변환 (예: '28/32' -> 28)
              const [success] = player.value.split('/');
              const numValue = parseInt(success);
              // 상대적 너비 계산 (최대값 대비 퍼센트)
              const barWidth = (numValue / maxValue) * 100;
              
              return (
                <div 
                  key={player.number} 
                  className={`${styles.playerItem} ${selectedPlayer === player.number ? styles.active : ''}`}
                  onClick={() => setSelectedPlayer(player.number)}
                >
                    <div className={styles.playerBarContainer}>
                        <span className={styles.playerNumber}>#{player.number}</span>
                        <div 
                            className={styles.playerBar} 
                            style={{ width: `${barWidth}%` }}
                        ></div>
                    </div>

                  <span className={styles.playerValue}>{player.value}</span>
                </div>
              );
            });
          })()}
          
          {activeTab === 'possession' && (() => {
            // 최대값 찾기 (초 단위 기준)
            const maxValue = Math.max(...POSSESSION_DATA.map(player => player.seconds));
            
            return POSSESSION_DATA.map((player) => {
              // 상대적 너비 계산 (최대값 대비 퍼센트)
              const barWidth = (player.seconds / maxValue) * 100;
              
              return (
                <div 
                  key={player.number} 
                  className={`${styles.playerItem} ${selectedPlayer === player.number ? styles.active : ''}`}
                  onClick={() => setSelectedPlayer(player.number)}
                >
                    <div className={styles.playerBarContainer}>
                        <span className={styles.playerNumber}>#{player.number}</span>
                        <div 
                            className={styles.playerBar} 
                            style={{ width: `${barWidth}%` }}
                        ></div>
                    </div>
                  <span className={styles.playerValue}>{player.value}</span>
                </div>
              );
            });
          })()}
        </div>
      </section>
    );
  };

  // TabContent 컴포넌트 수정
  const TabContent = ({ title, value, unit }) => {
    return (
      <div className={styles.tabContent}>
        <section className={styles.visualSection}>
          <h3>Match Analysis</h3>
          {activeTab === 'pass' ? (
            <div className={styles.passVisualContainer}>
              <div className={styles.networkBackground} />
              <div className={styles.passVisualOverlay}>
                {/* 중앙의 선택된 선수 */}
                <div className={`${styles.playerNode} ${styles.selectedNode}`}>
                  <span>{selectedPlayer}</span>
                </div>
                
                {/* 다른 선수들과의 연결 */}
                {PASS_INTERACTION_DATA[selectedPlayer]?.map((interaction, index) => {
                  const position = getPlayerPosition(index, interaction.distance);
                  return (
                    <div
                      key={interaction.to}
                      className={styles.playerConnection}
                      style={{
                        '--distance': `${position.distance}px`,
                        '--angle': `${position.angle}deg`
                      }}
                    >
                      {/* 보낸 패스 연결선 */}
                      <div 
                        className={`${styles.connectionLine} ${styles.sentLine}`}
                        style={{
                          '--thickness': `${1 + interaction.sent * 0.2}px`,
                        }}
                      />
                      {/* 받은 패스 연결선 */}
                      <div 
                        className={`${styles.connectionLine} ${styles.receivedLine}`}
                        style={{
                          '--thickness': `${1 + interaction.received * 0.2}px`,
                        }}
                      />
                      {/* 다른 선수 노드 */}
                      <div 
                        className={styles.playerNode}
                        style={{
                          '--size': `${getNodeSize(interaction.sent, interaction.received)}px`
                        }}
                      >
                        <span style={{ transform: `rotate(${-position.angle}deg)` }}>
                          {interaction.to}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className={styles.pitchContainer}>
              <img 
                src="https://ihcmlcnktfrrojtkrtbt.supabase.co/storage/v1/object/public/asset/plabai/pitch.png" 
                alt="축구장"
                className={styles.pitchImage}
              />
              {activeTab === 'position' && (
                <div className={styles.positionOverlay}>
                  {POSITION_DATA.map((player) => (
                    <div
                      key={player.number}
                      className={`${styles.playerPosition} ${styles[player.position.toLowerCase()]}`}
                    >
                      <span>{player.number}</span>
                    </div>
                  ))}
                </div>
              )}
              {activeTab === 'possession' && (
                <div className={styles.possessionOverlay}>
                  {/* 볼 운반 경로 화살표 */}
                  {selectedPlayer && ballMovementPathsRef.current[selectedPlayer]?.map((path, index) => (
                    <div 
                      key={index}
                      className={styles.ballMovementPath}
                      style={{
                        left: `${path.startX}%`,
                        top: `${path.startY}%`,
                        width: `${Math.sqrt(Math.pow(path.endX - path.startX, 2) + Math.pow(path.endY - path.startY, 2))}%`,
                        height: `${path.thickness}px`,
                        opacity: path.opacity,
                        transform: `rotate(${Math.atan2(path.endY - path.startY, path.endX - path.startX) * (180 / Math.PI)}deg)`,
                        transformOrigin: 'left center'
                      }}
                    >
                      <div className={styles.arrowHead}></div>
                    </div>
                  ))}
                </div>
              )}
              <div id="heatmap" className={styles.heatmapContainer}>
                {/* D3.js가 여기에 hexagon heatmap을 그립니다 */}
              </div>
              {activeTab === 'touch' && (
                <div className={styles.touchPoints}>
                  {touchPointsRef.current[selectedPlayer]?.map((point, index) => (
                    <div
                      key={index}
                      className={styles.touchPoint}
                      style={{
                        left: `${point.x}%`,
                        top: `${point.y}%`,
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          {(activeTab === 'touch' || activeTab === 'distance' || activeTab === 'pass' || activeTab === 'possession') && (
            <div className={styles.playerSelector}>
              {(activeTab === 'pass' ? PASS_DATA : 
                activeTab === 'touch' ? TOUCH_DATA : 
                activeTab === 'possession' ? POSSESSION_DATA :
                DISTANCE_DATA).map((player) => (
                <button
                  key={player.number}
                  className={`${styles.playerButton} ${selectedPlayer === player.number ? styles.active : ''}`}
                  onClick={() => setSelectedPlayer(player.number)}
                >
                  <span>{player.number}</span>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* TeamStats 컴포넌트 사용 */}
        <TeamStats 
          activeTab={activeTab} 
          selectedPlayer={selectedPlayer} 
          setSelectedPlayer={setSelectedPlayer} 
        />
      </div>
    );
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (isModalOpen) {
      // 모달이 열릴 때 body 스크롤 막기
      document.body.style.overflow = 'hidden';
    } else {
      // 모달이 닫힐 때 body 스크롤 복원
      document.body.style.overflow = 'unset';
    }

    // 컴포넌트가 언마운트될 때 스크롤 복원
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSaveImage = () => {
    // 이미지 저장 로직 구현
    console.log("이미지 저장");
  };

  const [count, setCount] = useState(0);
  const targetNumber = 8.3;
  const duration = 2000;
  
  // 탭 중앙 정렬 함수
  const centerActiveTab = () => {
    setTimeout(() => {
      const tabElement = document.querySelector(`[data-tab="${activeTab}"]`);
      const tabsContainer = document.querySelector(`.${styles.tabs}`);
      
      if (tabElement && tabsContainer) {
        const containerWidth = tabsContainer.offsetWidth;
        const tabWidth = tabElement.offsetWidth;
        const tabLeft = tabElement.offsetLeft;
        
        const scrollPosition = tabLeft - (containerWidth / 2) + (tabWidth / 2);
        tabsContainer.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  // 초기 로드와 탭 변경 시 중앙 정렬
  useEffect(() => {
    centerActiveTab();
  }, [activeTab]);

  return (
    <div className={styles.wrapper}>
      <Helmet>
        <title>plabAI</title>
        <meta name="AI가 분석한 나의 플레이와 하이라이트를 확인해보세요." content="plabAI" />
      </Helmet>
      <div className={styles.container}>
        {/* 헤더 */}
        <header className={styles.header}>
          <div className={styles.videoBackground}>
            <video
              autoPlay
              muted
              loop
              playsInline
              className={styles.video}
            >
              <source src="https://ihcmlcnktfrrojtkrtbt.supabase.co/storage/v1/object/public/asset/videos/demo.webm" type="video/mp4" />
            </video>
            <div className={styles.overlay} />
          </div>
            <button className={styles.imageSaveButton} onClick={handleOpenModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d={ICON_PATHS.image}
                fill="currentColor"
              />
            </svg>
            </button>
          <div className={styles.headerContent}>
            <div className={styles.rating}>
                <div className={styles.matchInfoContainer}>
              <span className={styles.matchInfo}>2025년 3월 10일 월요일 19:00</span>
              <span className={styles.matchInfo}>플랩스타디움 가산 디지털 엠파이어 2구장</span>
                </div>
              <span className={styles.ratingLabel}>MATCH RATING</span>
              <span className={styles.ratingValue}>8.9</span>
            </div>
            <a href="/plabai/highlight" className={styles.highlightButton}>
              하이라이트 재생
            </a>
          </div>
        </header>

        {/* 모달 */}
        {isModalOpen && (
          <div className={styles.modal}>
            <div className={styles.modalContent}>
              <button className={styles.closeButton} onClick={handleCloseModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                    d={ICON_PATHS.close}
                    fill="currentColor"
                />
                </svg>
              </button>
              <div className={styles.shareCard}>
                <img 
                  src="https://ihcmlcnktfrrojtkrtbt.supabase.co/storage/v1/object/public/asset/plabai/shrecard.png" 
                  alt="공유 카드"
                  className={styles.shareCardImage}
                />
              </div>
              <button className={styles.saveButton} onClick={handleSaveImage}>이미지로 저장하기</button>
            </div>
          </div>
        )}

        {/* 탭바 */}
        <div className={styles.tabBarContainer}>
          <div className={styles.tabs}>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                data-tab={tab.id}
                className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
                onClick={() => handleTabClick(tab.id)}
              >
                <span className={styles.tabLabel}>{tab.label}</span>
                <span className={styles.tabValue}>{tab.value}</span>
              </button>
            ))}
          </div>
        </div>

        {/* 탭 콘텐츠 */}
        <div className={styles.content}>
          {activeTab === 'distance' && (
            <TabContent title="Distance" value="8.9" unit="km" />
          )}
          {activeTab === 'possession' && (
            <TabContent title="Possession" value="3:22" unit="min" />
          )}
          {activeTab === 'pass' && (
            <TabContent title="Pass" value="76" unit="%" />
          )}
          {activeTab === 'touch' && (
            <TabContent title="Touch" value="42" unit="회" />
          )}
          {activeTab === 'position' && (
            <TabContent title="Position" value="LOW" />
          )}
        </div>
      </div>
    </div>
  );
}

export default PlabAI; 